import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LockOutlined, RightOutlined, RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import AgentAvatar from '../../components/AgentAvatar';
import '../styles/agent-sidebar.css';

const getAgentTag = (agent) => {
  if (agent?.role?.trim()) return agent.role.trim();
  const name = agent?.name || '';
  const parts = name.split(/\s+/);
  if (parts.length > 1) return parts.slice(1).join(' ');
  return '';
};

const getAgentDisplayName = (agent) => {
  const name = agent?.name || '';
  const parts = name.split(/\s+/);
  return parts[0] || name;
};

const getAgentDescription = (agent) => {
  if (agent?.intro?.trim()) {
    const text = agent.intro.trim();
    return text.length > 56 ? `${text.slice(0, 56)}…` : text;
  }
  return '';
};

const STATUS_CONFIG = {
  active: { tagClass: '', statusClass: '', labelKey: 'active' },
  expired: { tagClass: 'agent-card__tag--warning', statusClass: 'agent-card__status--warning', labelKey: 'expired' },
  locked: { tagClass: 'agent-card__tag--locked', statusClass: 'agent-card__status--locked', labelKey: 'locked' },
};

const resolveAgentStatus = (agent, openedAgents, expiredAgents) => {
  if (openedAgents.some((a) => a.id === agent.id)) return 'active';
  if (expiredAgents.some((a) => a.id === agent.id)) return 'expired';
  return 'locked';
};

const BUBBLE_GAP = 8;
const BUBBLE_WIDTH = 368;

const AgentPickerGridItem = memo(({ agent, status, selected, onClick, t }) => {
  const displayName = getAgentDisplayName(agent);
  const tag = getAgentTag(agent);
  const statusLabel = status === 'active'
    ? t('common.agentStatusActive', '就绪')
    : status === 'expired'
      ? t('common.expired')
      : t('common.notOpened');

  return (
    <button
      type="button"
      className={[
        'agent-picker-grid__item',
        selected && 'agent-picker-grid__item--selected',
        status === 'locked' && 'agent-picker-grid__item--locked',
        status === 'expired' && 'agent-picker-grid__item--expired',
      ].filter(Boolean).join(' ')}
      onClick={() => onClick(agent)}
    >
      <div className="agent-picker-grid__avatar-wrap">
        <AgentAvatar agent={agent} size={32} selected={selected} />
        {status === 'locked' && (
          <span className="agent-picker-grid__badge agent-picker-grid__badge--lock" aria-hidden>
            <LockOutlined />
          </span>
        )}
        {status === 'active' && (
          <span className="agent-picker-grid__badge agent-picker-grid__badge--online" aria-hidden />
        )}
      </div>
      <span className="agent-picker-grid__name" title={agent.name}>{displayName}</span>
      {tag ? (
        <span className="agent-picker-grid__tag">{tag}</span>
      ) : (
        <span className={`agent-picker-grid__status agent-picker-grid__status--${status}`}>
          {statusLabel}
        </span>
      )}
    </button>
  );
});

AgentPickerGridItem.displayName = 'AgentPickerGridItem';

const AgentPickerSection = memo(({
  title,
  count,
  agents,
  status,
  currentAgentId,
  onSelect,
  t,
}) => {
  if (!agents.length) return null;

  return (
    <div className="agent-picker-bubble__section">
      <div className="agent-picker-bubble__section-head">
        <span className="agent-picker-bubble__section-title">{title}</span>
        <span className="agent-picker-bubble__section-count">{count}</span>
      </div>
      <div className="agent-picker-grid agent-picker-grid--cols-4">
        {agents.map((agent) => (
          <AgentPickerGridItem
            key={agent.id}
            agent={agent}
            status={status}
            selected={currentAgentId === agent.id}
            onClick={onSelect}
            t={t}
          />
        ))}
      </div>
    </div>
  );
});

AgentPickerSection.displayName = 'AgentPickerSection';

const AgentPickerBubble = memo(({
  anchorRef,
  openedAgents,
  expiredAgents,
  noPermissionAgents,
  currentAgentId,
  onSelect,
  onClose,
  t,
}) => {
  const [position, setPosition] = useState(null);
  const totalCount = openedAgents.length + expiredAgents.length + noPermissionAgents.length;

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    setPosition({
      top: rect.top,
      left: rect.right + BUBBLE_GAP,
      arrowTop: Math.min(Math.max(rect.height * 0.42, 18), 28),
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  useLayoutEffect(() => {
    updatePosition();
  }, [totalCount, updatePosition]);

  const handleSelect = useCallback((agent) => {
    onSelect(agent);
    onClose();
  }, [onClose, onSelect]);

  if (!position) return null;

  return createPortal(
    <>
      <div className="agent-picker-bubble__backdrop" onClick={onClose} aria-hidden />
      <div
        className="agent-picker-bubble agent-picker-bubble--fixed"
        role="dialog"
        aria-label={t('common.selectAgent', '选择智能体')}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${BUBBLE_WIDTH}px`,
          '--agent-picker-arrow-top': `${position.arrowTop}px`,
        }}
      >
        <div className="agent-picker-bubble__arrow" aria-hidden />
        <div className="agent-picker-bubble__header">
          <div className="agent-picker-bubble__title">{t('common.selectAgent', '选择智能体')}</div>
        </div>
        <div className="agent-picker-bubble__sections">
          <AgentPickerSection
            title={t('common.agentAvailable', '可用助手')}
            count={openedAgents.length}
            agents={openedAgents}
            status="active"
            currentAgentId={currentAgentId}
            onSelect={handleSelect}
            t={t}
          />
          <AgentPickerSection
            title={t('common.expired')}
            count={expiredAgents.length}
            agents={expiredAgents}
            status="expired"
            currentAgentId={currentAgentId}
            onSelect={handleSelect}
            t={t}
          />
          <AgentPickerSection
            title={t('common.agentLocked', '待开通')}
            count={noPermissionAgents.length}
            agents={noPermissionAgents}
            status="locked"
            currentAgentId={currentAgentId}
            onSelect={handleSelect}
            t={t}
          />
        </div>
      </div>
    </>,
    document.body,
  );
});

AgentPickerBubble.displayName = 'AgentPickerBubble';

const SelectedAgentCard = memo(({
  agent,
  status,
  pickerOpen,
  onTogglePicker,
  canOpenPicker,
  openedAgents,
  expiredAgents,
  noPermissionAgents,
  currentAgentId,
  onAgentClick,
  onClosePicker,
  t,
}) => {
  const anchorRef = useRef(null);
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  const displayName = getAgentDisplayName(agent);
  const tag = getAgentTag(agent);
  const desc = getAgentDescription(agent);
  const statusLabel = status === 'active'
    ? t('common.agentStatusActive', '就绪')
    : status === 'expired'
      ? t('common.expired')
      : t('common.notOpened');
  const totalPickerAgents = openedAgents.length + expiredAgents.length + noPermissionAgents.length;

  return (
    <div className="agent-sidebar__picker-anchor">
      <div
        ref={anchorRef}
        role="button"
        tabIndex={0}
        className={[
          'agent-card',
          'agent-card--compact',
          'agent-card--selected',
          pickerOpen && 'agent-card--picker-open',
        ].filter(Boolean).join(' ')}
        onClick={canOpenPicker ? onTogglePicker : undefined}
        onKeyDown={(e) => {
          if (!canOpenPicker) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTogglePicker();
          }
        }}
      >
        <div className="agent-card__avatar-wrap">
          <AgentAvatar agent={agent} size={40} selected />
          {status === 'active' && <span className="agent-card__online" aria-hidden />}
          {status === 'locked' && (
            <span className="agent-card__status-badge agent-card__status-badge--lock" aria-hidden>
              <LockOutlined />
            </span>
          )}
        </div>
        <div className="agent-card__content">
          <div className="agent-card__name-row">
            <span className="agent-card__name" title={agent.name}>{displayName}</span>
            {tag && (
              <span className={`agent-card__tag ${cfg.tagClass}`.trim()}>{tag}</span>
            )}
          </div>
          {desc && <div className="agent-card__desc">{desc}</div>}
          <div className="agent-card__meta">
            <span className={`agent-card__status ${cfg.statusClass}`.trim()}>
              {status !== 'active' && status === 'locked' && <LockOutlined style={{ fontSize: 10 }} />}
              {status === 'active' && <span className="agent-card__status-dot" />}
              {statusLabel}
            </span>
          </div>
        </div>
        {canOpenPicker && (
          <RightOutlined className={`agent-card__chevron${pickerOpen ? ' agent-card__chevron--open' : ''}`} />
        )}
      </div>
      {pickerOpen && totalPickerAgents > 0 && (
        <AgentPickerBubble
          anchorRef={anchorRef}
          openedAgents={openedAgents}
          expiredAgents={expiredAgents}
          noPermissionAgents={noPermissionAgents}
          currentAgentId={currentAgentId}
          onSelect={onAgentClick}
          onClose={onClosePicker}
          t={t}
        />
      )}
    </div>
  );
});

SelectedAgentCard.displayName = 'SelectedAgentCard';

const AgentSidebar = ({
  openedAgents = [],
  expiredAgents = [],
  noPermissionAgents = [],
  currentAgentId,
  onAgentClick,
  t,
  embedded = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const allAgents = useMemo(
    () => [...openedAgents, ...expiredAgents, ...noPermissionAgents],
    [expiredAgents, noPermissionAgents, openedAgents],
  );
  const currentAgent = allAgents.find((agent) => agent.id === currentAgentId)
    || openedAgents[0]
    || allAgents[0];
  const currentStatus = currentAgent
    ? resolveAgentStatus(currentAgent, openedAgents, expiredAgents)
    : 'active';

  const closePicker = useCallback(() => setPickerOpen(false), []);
  const canOpenPicker = allAgents.length > 1;
  const togglePicker = useCallback(() => {
    if (!canOpenPicker) return;
    setPickerOpen((prev) => !prev);
  }, [canOpenPicker]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePicker();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closePicker, pickerOpen]);

  useEffect(() => {
    closePicker();
  }, [closePicker, currentAgentId]);

  return (
    <div
      className={['agent-sidebar', embedded && 'agent-sidebar--embedded'].filter(Boolean).join(' ')}
    >
      <div className="agent-sidebar__header">
        <div className="agent-sidebar__header-top">
          <div className="agent-sidebar__header-icon">
            <ThunderboltOutlined />
          </div>
          <div>
            <div className="agent-sidebar__title">{t('common.agentHub', 'AI 智能体')}</div>
            <div className="agent-sidebar__subtitle">Agent Workspace</div>
          </div>
        </div>
      </div>

      <div className="agent-sidebar__body">
        {currentAgent ? (
          <SelectedAgentCard
            agent={currentAgent}
            status={currentStatus}
            pickerOpen={pickerOpen}
            onTogglePicker={togglePicker}
            canOpenPicker={canOpenPicker}
            openedAgents={openedAgents}
            expiredAgents={expiredAgents}
            noPermissionAgents={noPermissionAgents}
            currentAgentId={currentAgentId}
            onAgentClick={onAgentClick}
            onClosePicker={closePicker}
            t={t}
          />
        ) : (
          <div className="agent-sidebar__empty">
            <RobotOutlined style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }} />
            <div>{t('common.noAvailableAgents')}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(AgentSidebar);
